import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CoreProvider } from '../core.provider';
import { LoginRequestDto } from '../api/models';
import { JwtPayload, SessionDtoExtended } from './session-dto-extended';
import { Mutex } from './mutex';
import { setTag, setUser } from '@sentry/angular';

@Injectable({
  providedIn: "root",
})
export class AuthService {

  public async getLiveTokens(): Promise<SessionDtoExtended | undefined> {
    const tokensData = await this.core.storage.getItem('live-tokens');
    if (!tokensData) return undefined;
    try {
      return JSON.parse(tokensData);
    } catch (err) {
      console.error('Error parsing stored live tokens', err);
      return undefined;
    }
  }

  private userData?: JwtPayload | null = null;
  public get user(): JwtPayload | undefined {
    return this.userData || undefined;
  }
  public get isAuthenticated(): boolean | null {
    if (this.userData === null) return null;
    return !!this.userData;
  }
  /** True when the currently authenticated user is a superadmin (role SUPER_ADMIN). */
  public get isSuperadmin(): boolean {
    return this.userData?.role === 'SUPER_ADMIN';
  }
  /** True when the current session is a superadmin impersonating another user. */
  private impersonationActive = false;
  public get isImpersonating(): boolean {
    return this.impersonationActive;
  }

  /**
   * Impersonation is restricted to the PWA (web) deployment channel.
   *
   * Two reasons: the session swap relies on a hard `window.location` reload, which the Cordova
   * WebViews (custom `haibench://` / `https://localhost` schemes with no SPA fallback) do not
   * serve for app route paths; and it wipes the local store for data isolation, which on a native
   * device could destroy game recordings that are not yet synced to the cloud.
   */
  public get canImpersonate(): boolean {
    return this.isSuperadmin && this.core.isWeb;
  }

  private static readonly SUPERADMIN_SESSION_BACKUP_KEY = 'superadmin-session-backup';

  /**
   * Starts impersonating the given user with FULL session isolation:
   *   1. Snapshots the superadmin's entire local session (tokens + all local data) into a
   *      separate backup vault so it can be fully restored later.
   *   2. Wipes local storage (behaves like a full logout) so no superadmin data leaks in.
   *   3. Loads the impersonated user's token and forces a full reload, so the app boots clean
   *      and re-syncs everything from the impersonated user's cloud account.
   *
   * Web/PWA only — see `canImpersonate`.
   */
  async impersonate(userUuid: string): Promise<void> {
    if (!this.isSuperadmin) throw new Error('Only superadmins can impersonate users');
    if (!this.canImpersonate) throw new Error('Impersonation is only available on the web app');

    const sess = await firstValueFrom(this.core.api.superadmin.superadminControllerImpersonate({ uuid: userUuid }));
    const session = new SessionDtoExtended(sess);

    // 1) Back up the full superadmin session. Only if one doesn't already exist, so a token
    //    refresh mid-impersonation can never clobber the real backup. Throws (before anything is
    //    wiped) if the snapshot cannot be taken.
    if (!(await this.core.storage.hasSessionBackup(AuthService.SUPERADMIN_SESSION_BACKUP_KEY))) {
      await this.core.storage.backupSession(AuthService.SUPERADMIN_SESSION_BACKUP_KEY);
    }

    // 2) Full clear for isolation, then 3) install the impersonated session. If installing the
    //    token fails the store is already wiped and nothing would be able to reach the backup
    //    again, so roll it back before surfacing the error.
    try {
      await this.core.storage.reset();
      await this.core.storage.setItem('live-tokens', JSON.stringify(session));
    } catch (err) {
      console.error('Error installing impersonated session, rolling back', err);
      await this.core.storage.restoreSession(AuthService.SUPERADMIN_SESSION_BACKUP_KEY);
      throw err;
    }

    // Hard reload so every in-memory service re-hydrates from the clean, impersonated store and
    // syncs the impersonated user's data from the cloud.
    window.location.href = '/game-tracker/past-games';
  }

  /**
   * Ends impersonation and fully restores the superadmin session. Wipes the impersonated user's
   * local data (isolation), restores the backed-up superadmin session, then reloads.
   */
  async stopImpersonating(): Promise<void> {
    const restored = await this.core.storage.restoreSession(AuthService.SUPERADMIN_SESSION_BACKUP_KEY);
    if (!restored) {
      // No backup to restore — fall back to a clean logout so we never leave a broken session.
      this.impersonationActive = false;
      await this.logout();
      window.location.href = '/login';
      return;
    }
    window.location.href = '/superadmin/users';
  }

  private core!: CoreProvider;
  async initCore(core: CoreProvider) {
    this.core = core;
    await this.initialiseAuth();
  }

  async initialiseAuth() {
    const tokens = await this.getLiveTokens();
    if (tokens) {
      this.userData = tokens.accessTokenParsed;
      setUser({
        email: this.userData.email,
        name: this.userData.name,
        role: this.userData.role,
        id: this.userData.sub,
      });
      setTag('user.id', this.userData.sub);
      setTag('user.display', `${this.userData.name} (${this.userData.email})`);
      setTag('platform', this.core.isAndroid ? 'android' : this.core.isIOS ? 'ios' : this.core.isWeb ? 'web' : 'unknown');
      // Impersonation is active if the token still carries the claim OR a session backup exists
      // (the latter survives an access-token refresh that would drop the claim). Web only, so a
      // native boot never even opens the backup vault.
      this.impersonationActive = this.core.isWeb && (
        !!this.userData.impersonatedBy ||
        await this.core.storage.hasSessionBackup(AuthService.SUPERADMIN_SESSION_BACKUP_KEY)
      );
      console.log('User data', this.userData);
    } else {
      this.userData = undefined;
      this.impersonationActive = false;
      return;
    }
  }

  async login(login: LoginRequestDto): Promise<SessionDtoExtended> {
    const sess = await firstValueFrom(this.core.api.auth.authControllerLogin({body: login})); // Can throw an error to caller
    console.log('Login response', sess);
    const session = new SessionDtoExtended(sess);
    await this.core.storage.reset();
    await this.core.storage.setItem('live-tokens', JSON.stringify(session));
    console.log('Stored live tokens', session, await this.core.storage.getItem('live-tokens'), await this.getLiveTokens());
    await this.initialiseAuth();
    this.core.api.checkSubscriptionStatus();
    return session;
  }

  async refreshTokens(): Promise<false|SessionDtoExtended> {
    const {unlock, previousParams} = await Mutex.getLock('auth-refresh-tokens').lock(false);

    if (previousParams === null) {
      console.log('No previous refresh result, trying to refresh tokens');
      let result: false|SessionDtoExtended = false;
      try {
        const tokens = await this.getLiveTokens();
        if (tokens) {
          const sess = await firstValueFrom(this.core.api.auth.authControllerRefreshToken({body: {refreshToken: tokens.refreshToken}}));
          const session = new SessionDtoExtended(sess);
          await this.core.storage.setItem('live-tokens', JSON.stringify(session));
          console.log('Stored live tokens', session, await this.core.storage.getItem('live-tokens'), await this.getLiveTokens());
          await this.initialiseAuth();
          result = session;
        }

      } catch (err) {
        console.error('Error refreshing tokens', err);
        result = false;
      } finally {
        unlock(result);
        return result;
      }
    } else {
      console.log('Returning previous refresh result from another request');
      unlock(previousParams);
      return previousParams as false|SessionDtoExtended;
    }
  }

  async logout() {
    await this.core.storage.removeItem('live-tokens');
    if (this.core.isWeb) await this.core.storage.clearSessionBackup(AuthService.SUPERADMIN_SESSION_BACKUP_KEY);
    await this.core.storage.removeItem('last-billing-status');
    this.core.api.billingStatus$.next({isActive: false, status: 'free', freeGamesRemaining: 3});
    this.userData = undefined;
    this.impersonationActive = false;
    this.core.router.navigate(['/login']);
  }

}